/* eslint-disable no-useless-catch */
import { nanoid } from 'nanoid';
import { uploadData, remove as storageRemove } from 'aws-amplify/storage';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  deleteCaseFile,
  deleteFileData,
  updateCompany,
  updateForeignNational,
} from 'graphql/mutations';
import {
  createFileData,
  updateFileData,
} from 'graphql/customFileQueries';
import {
  Case,
  Company,
  CreateFileDataMutation,
  FileData,
  FileRequirement,
  FileRequirementInput,
  FileType,
  ForeignNational,
  ListCaseFilesQuery,
  Role,
  S3Object,
  UpdateCompanyMutation,
} from 'API';
import { FileCategory, S3Level } from 'models';
import { getCase, getCompany, getForeignNational } from 'graphql/queries';
import {
  caseCompanyData, listCaseFiles, updateCase,
} from 'graphql/customCaseQueries';
import { updateCompanyForProfile } from 'graphql/companyProfileQueries';
import AuthService from 'services/auth';
import { ALREADY_DELETED_FILETYPE, FILE_TYPES } from 'shared/constants';
import { labels } from 'shared/labels/fileLabels';
import { asciiMetadataValue, requireExpirationDate, requireIssueDate } from 'shared/utilities';
import { STORAGE_BUCKET, STORAGE_REGION } from './storage';
import { client } from 'lib/graphql-client';
import { GraphQLResult } from '@aws-amplify/api';
import { get, post } from 'aws-amplify/api';

/**
 * Strip `__typename` (and any other non-input meta-fields) from each FileRequirement
 * before sending it back to AppSync as a `FileRequirementInput`. AppSync validates
 * inputs strictly and rejects unknown fields like `__typename`.
 */
export const toFileRequirementInput = (
  requirements: Array<any> | null | undefined,
): Array<FileRequirementInput> => (requirements || []).map(
  ({ __typename, ...rest }) => rest as FileRequirementInput,
);

interface ZipFile {
  fileName: string;
  key: string;
  level: string;
  identityId?: string;
}

interface CaseFileType {
  __typename: string;
  id: string;
  fileID: string;
}

export const downloadInBrowser = async (URL: string, filename: string) => {
  const download = await fetch(URL, {
    method: 'GET',
  });

  // An expired presigned URL returns 403 with an XML body, which would
  // otherwise be saved to disk under the document's name.
  if (!download.ok) {
    throw new Error(`Download failed: ${download.status} ${download.statusText}`);
  }

  const blob = await download.blob();
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

class DocumentService {
  private API = {
    name: 'documentsapi',
    documentsPath: '/documents',
    zippingPath: '/zipping',
  };

  /**
   * Requests a ZIP of the given files. Returns as soon as the job is accepted;
   * the archive is built in the background and emailed to the requester, so
   * there is no file to hand back here.
   */
  public zipFiles = async (files: ZipFile[], description?: string): Promise<ZipRequestResponse> => {
    const cogToken = (await fetchAuthSession()).tokens?.idToken?.toString() ?? '';

    const headers = {
      Authorization: cogToken,
    };

    const body = { files, description };
    const payload = { body, headers, authMode: 'iam' as const };

    const { body: zipBody } = await post({ apiName: this.API.name, path: this.API.zippingPath, options: payload as any }).response;

    return await zipBody.json() as unknown as ZipRequestResponse;
  };

  /**
   * getDocument fetches a specific S3 object from then given targetCognitoUsername
   *
   * @param { string } documentKey S3 document key
   * @param { string } targetCognitoUsername Target's Cognito Username
   * @param { string } tempUserId User's temporary id
   * @returns DocumentAPIResponse
   */
  public getDocument = async (file: S3Object): Promise<DocumentAPIResponse> => {
    const cogToken = (await fetchAuthSession()).tokens?.idToken?.toString() ?? '';

    const headers = {
      Authorization: cogToken,
    };

    const queryParams = {
      file: JSON.stringify(file),
    };

    const payload = { queryParams, headers, authMode: 'iam' as const };

    const { body: docBody } = await get({ apiName: this.API.name, path: this.API.documentsPath, options: payload as any }).response;

    return docBody.json() as unknown as DocumentAPIResponse;
  };

  public deleteFileData = async (
    id: string,
  ) => client.graphql({
    query: deleteFileData,
    variables: { input: { id } },
  });

  public downloadDocument = async (fileData: FileData) => {
    const { fileName, original } = fileData;

    if (!original) {
      return;
    }

    try {
      const response = await this.getDocument(original);

      if (!response?.signedUrl) {
        throw new Error('Unauthorized');
      }

      await downloadInBrowser(
        response.signedUrl,
        fileName || response.fileName,
      );
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.log(err);
      throw err;
    }
  };

  public filesMatchingCategory = (
    files: Array<FileData>,
    fileCategory: FileCategory | string,
  ): Array<FileData> => files.filter((file) => {
    if (file?.fileType === FileType.CUSTOM) {
      return file?.fileCategory && file.fileCategory === fileCategory;
    }

    return (
      (file.fileType && this.typeToCategory(file.fileType as FileType) === fileCategory)
      || (fileCategory === FileCategory.OTHER && file.fileType == null)
    );
  });

  public fileRequirementsMatchingCategory = (
    requirements: Array<FileRequirement>,
    fileCategory: FileCategory | string,
  ): Array<FileRequirement> => requirements.filter((requirement) => {
    if (requirement?.type === FileType.CUSTOM) {
      return requirement?.fileCategory && requirement.fileCategory === fileCategory;
    }

    return requirement.type && this.typeToCategory(requirement.type) === fileCategory;
  });

  public filesMatchingType = (
    files: Array<FileData>,
    fileType: FileType | string,
  ): Array<FileData> => files.filter((file) => {
    if (file?.fileType === FileType.CUSTOM) {
      return file?.fileRequirement && file.fileRequirement === fileType;
    }

    return (file.fileType && file.fileType === fileType)
    || (fileType === FileType.OTHER && file.fileType == null);
  });

  /**
   * The files the Documents tab actually shows: `ProfileDocumentsCard` renders
   * nothing for a category with no matching requirement, and within a category
   * only files whose type matches one of those requirements. "Download all as
   * ZIP" has to resolve to the same set — handing the raw `files.items` array to
   * the zipper shipped documents that had never been on screen.
   */
  public filesForDownload = (
    files: Array<FileData>,
    requirements: Array<FileRequirement>,
    categories: Array<{ category: FileCategory | string, isCustomCategory?: boolean }>,
    isForeignNational?: boolean,
  ): Array<FileData> => {
    const seen = new Set<string>();

    return categories.flatMap(({ category, isCustomCategory }) => {
      const categoryRequirements = this.fileRequirementsMatchingCategory(requirements, category);

      if (!categoryRequirements.length) {
        return [];
      }

      const categoryFiles = this.filesMatchingCategory(files, category);

      return categoryRequirements.flatMap((requirement) => {
        const type = (isCustomCategory ? requirement.fileType : requirement.type)!;
        const matching = this.filesMatchingType(categoryFiles, type);

        // ProfileDocumentsCardRequirement hides a company-owned "Other" document
        // from a national viewing a case, so the archive has to hide it too.
        if (type === FileType.OTHER && isForeignNational) {
          return matching.filter((file) => file.companyID === null);
        }

        return matching;
      });
    }).filter((file) => {
      if (!file?.id || seen.has(file.id)) {
        return false;
      }
      seen.add(file.id);

      return true;
    });
  };

  public getLabel = (fieldName: string): string => labels[fieldName] || '';

  public mapFileDataToZipFile = (file: FileData):ZipFile => ({
    fileName: file.fileName!,
    key: file!.original!.key!,
    level: file!.original!.level!.toLowerCase() || 'public',
    identityId: file!.original?.identityId || undefined,
  });

  public markFileRequirementCompletion = async ({
    id,
    fileType,
    isComplete,
    containerType,
    setter,
    role,
    isCustomRequirement,
  }: MarkFileRequirementCompletionInput<Company | ForeignNational | Case>) => {
    try {
      let fileRequirements: Array<FileRequirementInput> = [];
      let query: string;
      let mutation: string;
      let key: string;

      const type = isCustomRequirement ? 'fileType' : 'type';

      // 1. Get the current file requirements from the record in question
      switch (containerType) {
        case 'case': {
          query = getCase;
          mutation = updateCase;
          key = 'Case';
          break;
        }

        case 'company':
          query = getCompany;
          mutation = updateCompanyForProfile;
          key = 'Company';
          break;

        case 'national':
          query = getForeignNational;
          mutation = updateForeignNational;
          key = 'ForeignNational';
          break;

        default:
          throw new Error();
      }

      const queryResponse = (await client.graphql({
        query: query,
        variables: { id },
      })) as any;

      fileRequirements = [
        ...((queryResponse.data[`get${key}`].fileRequirements
          || []) as Array<FileRequirementInput>),
      ];

      // 2. Set the correct value
      const fileIndex = fileRequirements.findIndex(
        (req) => req[type] === fileType,
      );

      if (fileIndex < 0) {
        throw new Error();
      }

      fileRequirements[fileIndex].isApproved = isComplete;

      const variables: any = {
        input: { id, fileRequirements: toFileRequirementInput(fileRequirements) },
      };
      if (containerType === 'case') {
        variables.messagesUserGroup = AuthService.roleToUserGroup(role);
      }

      const updateResponse = (await client.graphql({
        query: mutation,
        variables: variables,
      })) as any;

      // 3. Update the state
      if (updateResponse.data[`update${key}`]?.fileRequirements) {
        setter((draft: any) => {
          draft.fileRequirements = updateResponse.data[`update${key}`].fileRequirements;
        });

        return;
      }

      throw new Error();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      throw new Error();
    }
  };

  public deleteFileType = async ({
    id,
    fileType,
    containerType,
    setter,
    role,
    isCustomRequirement,
  }: DeleteFileRequirementTypeInput<Company | ForeignNational | Case>) => {
    try {
      let fileRequirements: Array<FileRequirementInput> = [];
      let query: string;
      let mutation: string;
      let key: string;

      const type = isCustomRequirement ? 'fileType' : 'type';

      // 1. Get the current file requirements from the record in question
      switch (containerType) {
        case 'case': {
          query = getCase;
          mutation = updateCase;
          key = 'Case';
          break;
        }

        case 'company':
          query = getCompany;
          mutation = updateCompanyForProfile;
          key = 'Company';
          break;

        case 'national':
          query = getForeignNational;
          mutation = updateForeignNational;
          key = 'ForeignNational';
          break;

        default:
          throw new Error();
      }

      const queryResponse = (await client.graphql({
        query: query,
        variables: { id },
      })) as any;

      fileRequirements = [
        ...((queryResponse.data[`get${key}`].fileRequirements
          || []) as Array<FileRequirementInput>),
      ];

      // 2. Set the correct value

      if (containerType === 'national') {
        // check if File Type already deleted means hidden true or not if its already deleted then we need to show the error message
        // Error Message: This Document Type can only be removed from the Open Case
        const isDeleted = fileRequirements?.find((res) => (res?.[type] === fileType && res.hidden));

        if (isDeleted) {
          return ALREADY_DELETED_FILETYPE;
        }

        const updatedFileRequirements = fileRequirements?.map((file) => {
          if (file && file[type] === fileType) {
            // eslint-disable-next-line no-param-reassign
            file.hidden = true;
          }

          return file;
        });

        fileRequirements = updatedFileRequirements;
      } else {
        fileRequirements = fileRequirements.filter((file:FileRequirementInput) => file[type] !== fileType);
      }

      const variables: any = {
        input: { id, fileRequirements: toFileRequirementInput(fileRequirements) },
      };
      if (containerType === 'case') {
        variables.messagesUserGroup = AuthService.roleToUserGroup(role);
      }

      const updateResponse = (await client.graphql({
        query: mutation,
        variables: variables,
      })) as any;

      // 3. Update the state
      if (updateResponse.data[`update${key}`]?.fileRequirements) {
        setter((draft: any) => {
          draft.fileRequirements = updateResponse.data[`update${key}`].fileRequirements;
        });

        return updateResponse;
      }

      throw new Error();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      throw new Error();
    }
  };

  /**
   * putDocument creates an S3 object and returns a FileData record
   *
   * @param { File } file the file to upload/update
   * @param { string} uploaderID The Dynamo ID of person who uploaded file
   * @param { S3ObjectMetadata } metadata S3 metadata to put in file
   * @param { DocumentSource } sourcing Information about who and what this
   *                                    file belongs to
   * @returns DocumentAPIResponse
   */
  public putDocument = async (
    file: File,
    uploaderID: string,
    metadata: S3ObjectMetadata,
    storage?: DocumentStorage,
    role?: Role | undefined,
    progressCallback?: (progress: any) => void,
  ): Promise<FileData> => {
    // Remove null or undefined values from metadata
    // so they don’t cause put to fail.
    Object.keys(metadata).forEach(
      // eslint-disable-next-line no-param-reassign
      (k) => (metadata as any)[k] == null && delete (metadata as any)[k],
    );

    try {
      const key = `${nanoid()}/${file.name}`;
      const level = S3Level.PRIVATE;

      // Every value here becomes an `x-amz-meta-*` header, which S3 only
      // accepts in ASCII — see asciiMetadataValue. Sanitize a copy so the
      // untouched `metadata` still feeds the FileData record below.
      const s3Metadata = Object.entries(metadata).reduce(
        (acc, [k, v]) => ({ ...acc, [k]: asciiMetadataValue(String(v)) }),
        {} as Record<string, string>,
      );

      await uploadData({
        key,
        data: file,
        options: {
          accessLevel: level.toLowerCase() === 'public' ? 'guest' : level.toLowerCase() as 'private' | 'protected',
          contentType: file.type,
          metadata: s3Metadata,
          ...(progressCallback && {
            onProgress: ({ transferredBytes, totalBytes }: { transferredBytes: number; totalBytes?: number }) =>
              progressCallback({ loaded: transferredBytes, total: totalBytes }),
          }),
        },
      }).result;

      const identityId = (await fetchAuthSession()).identityId ?? '';

      const original = {
        region: STORAGE_REGION,
        bucket: STORAGE_BUCKET,
        key,
        level: S3Level.PRIVATE,
        identityId,
      };

      let caseResponse: any = {};
      let fileRequirementExist: FileRequirement[] = [];
      let companyId: string = '';
      let requestProcess: string = '';

      // Only a type the catalogue calls company-stored belongs to the company.
      // `fileType === CUSTOM` used to qualify too, so every custom document type a
      // paralegal invented — an I-485 "Bona Fide" lease, a driving licence, a
      // utility bill — was stamped with the case's companyID. The dynamoTrigger
      // then linked each of those to every open case at that company, putting one
      // national's personal papers on their colleagues' cases.
      const foundedFile = FILE_TYPES.find((doc) => (doc.type === metadata?.fileType && doc.defaultStorage === 'company'));

      if (storage?.caseID && foundedFile) {
        caseResponse = (await client.graphql({
          query: caseCompanyData,
          variables: { id: storage.caseID },
        })) as GraphQLResult<any>;

        companyId = caseResponse?.data?.getCase?.company?.id;
        requestProcess = caseResponse?.data?.getCase?.requestedProcess;

        fileRequirementExist = caseResponse.data?.getCase?.company?.fileRequirements?.filter((fileReq: FileRequirement) => (fileReq?.type === metadata?.fileType));

        if (!fileRequirementExist?.length && role !== Role.FOREIGNNATIONAL && companyId) {
          await client.graphql({
            query: updateCompany,
            variables: {
              input: {
                id: companyId,
                fileRequirements: [...(caseResponse?.data?.getCase?.company?.fileRequirements || []), {
                  type: metadata.fileType,
                  expirationDateRequired: requireExpirationDate.includes(metadata?.fileType || ''),
                  issueDateRequired: requireIssueDate.includes(metadata?.fileType || ''),
                }],
              },
            },
          }) as GraphQLResult<UpdateCompanyMutation>;
        }
      }

      try {
        let companyID = {};

        // `!fileRequirementExist?.length` used to qualify as well, which meant any
        // type the company had no requirement for became a company file — the
        // common case, not the exception. Now it takes an explicit company upload
        // or a company-stored type.
        if ((storage?.companyID || foundedFile) && requestProcess !== 'H_4') {
          companyID = { companyID: storage?.companyID || companyId || null };
        }

        if (!storage?.caseID && storage?.ownerNationalID && !storage.companyID) {
          companyID = {};
        }

        if (storage?.caseID && storage?.ownerNationalID && companyId && requestProcess !== 'H_4') {
          companyID = { companyID: storage?.companyID || companyId || null };
        }

        if (storage?.caseID && storage?.ownerNationalID && !storage.companyID && !companyId) {
          companyID = {};
        }

        if (storage?.caseID && storage?.ownerNationalID && metadata.fileType === 'OTHER') {
          companyID = {};
        }

        const response = (await client.graphql({
          query: createFileData,
          variables: {
            input: {
              original,
              contentType: file.type,
              fileType: metadata.fileType,
              fileRequirement: metadata.fileRequirement,
              fileCategory: metadata?.fileCategory,
              fileName: file.name,
              uploadDate: new Date().toISOString(),
              uploaderID,
              uploaderEmployerID: uploaderID,
              uploaderForeignNationalID: uploaderID,
              uploaderTeamMemberID: uploaderID,
              ...(storage?.sourceID ? { sourceID: storage.sourceID } : {}),
              // ...(storage?.companyID ? { companyID: storage?.companyID } : {}),
              ...companyID,
              ...(storage?.ownerNationalID
                ? { ownerNationalID: storage.ownerNationalID }
                : {}),
              ...(storage?.processRequestID
                ? { processRequestID: storage.processRequestID }
                : {}),
            },
          },
        })) as GraphQLResult<CreateFileDataMutation>;

        if (response.data?.createFileData) {
          return response.data.createFileData as FileData;
        }

        throw new Error();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
        // If saving to database didn't work, remove from S3.
        await storageRemove({
          key,
          options: { accessLevel: level.toLowerCase() === 'public' ? 'guest' : level.toLowerCase() as 'private' | 'protected' },
        });

        throw new Error('Upload failed');
      }
    } catch (error) {
      throw error;
    }
  };

  removeFromCase = async (fileID: string, caseID: string) => {
    let nextToken: string | null = null;
    const caseFiles: (CaseFileType | null)[] | undefined = [];

    do {
      // eslint-disable-next-line no-await-in-loop
      const caseResponse = (await client.graphql({
        query: listCaseFiles,
        variables: { id: caseID, nextToken },
      })) as GraphQLResult<ListCaseFilesQuery>;
      const { items, nextToken: token = null } = caseResponse.data?.getCase?.files || {};
      caseFiles.push(...(items || []));
      nextToken = token;
    } while (nextToken);

    // A file can carry more than one connection to the same case (VOYAG-803
    // duplicates). Removing only the first left the other one behind, so the
    // document came back on the next load.
    const connectionIDs = caseFiles
      .filter((file) => file?.fileID === fileID)
      .map((file) => file?.id)
      .filter((id): id is string => Boolean(id));

    if (connectionIDs.length) {
      return Promise.all(connectionIDs.map((id) => client.graphql({
        query: deleteCaseFile,
        variables: {
          input: {
            id,
          },
        },
      })));
    }

    throw new Error();
  };

  removeFromCompany = async (fileID: string) => client.graphql({
    query: updateFileData,
    variables: {
      input: {
        id: fileID,
        companyID: null,
      },
    },
  });

  removeFromNational = async (fileID: string) => client.graphql({
    query: updateFileData,
    variables: {
      input: {
        id: fileID,
        ownerNationalID: null,
      },
    },
  });

  typeToCategory = (fileType?: FileType): FileCategory => {
    if (!fileType) {
      return FileCategory.OTHER;
    }

    if (FILE_TYPES.find((type) => type.type === fileType)) {
      return FILE_TYPES.find((type) => type.type === fileType)!.category;
    }

    return FileCategory.OTHER;
  };
}

export default new DocumentService();
